import { InfiniteTable } from '@/components/_common';
import GroupColumnFilter from '@/components/_common/SubGroup/SubGroupColumnFilter';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import ManufacturerColumnFilter from '@/components/_common/Manufacturer/ManufacturerColumnFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { PurchaseItems } from '@/core/domain/purchase/purchaseImport/PurchaseImport.types';
import { RegistrationsDefaultCode } from '@/types/Register';
import { getTableScrollX } from '@/utils/table';
import { ColumnsType } from 'antd/es/table/interface';
import React, { useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import { Input } from 'antd';

interface Props {
  setUnlinkItems: React.Dispatch<React.SetStateAction<PurchaseItems[]>>;
  unlinkItems: PurchaseItems[];
}

const ConfirmManufacturerAndSubGroup: React.FC<Props> = ({
  unlinkItems,
  setUnlinkItems,
}) => {
  const { data } = useMatrix();

  const [filterManufacturerDropdownOpen, setFilterManufacturerDropdownOpen] =
    useState(false);
  const [filterGroupDropdownOpen, setFilterGroupDropdownOpen] = useState(false);

  function setFieldValue([field, i]: [string, number], value: unknown) {
    setUnlinkItems((state) =>
      state.map((item) =>
        item.index === i ? { ...item, [field]: value } : item,
      ),
    );
  }

  function setManufacturerId(manufacturerId: number) {
    setUnlinkItems((state) =>
      state.map((item) => (!item.linked ? { ...item, manufacturerId } : item)),
    );

    setFilterManufacturerDropdownOpen(false);
  }

  function setSubGroupId(subGroupId: number) {
    setUnlinkItems((state) =>
      state.map((item) => (!item.linked ? { ...item, subGroupId } : item)),
    );

    setFilterGroupDropdownOpen(false);
  }

  const columnMapping = {
    [RegistrationsDefaultCode.internalCode]: {
      dataIndex: 'internalCode',
      title: 'Cód. Interno',
    },
    [RegistrationsDefaultCode.barCode]: {
      dataIndex: 'barCode',
      title: 'Cód. Barras',
    },
    [RegistrationsDefaultCode.manufacturerCode]: {
      dataIndex: 'productCode',
      title: 'Cód. Fabricante',
      render: (value: string, row: PurchaseItems) => (
        <Input
          defaultValue={value}
          onChange={({ target }) =>
            setFieldValue(['productCode', row.index], target.value)
          }
          disabled={row.linked}
        />
      ),
    },
    [RegistrationsDefaultCode.companyCode]: {
      dataIndex: 'companyCode',
      title: 'Cod. Empresa',
    },
    [RegistrationsDefaultCode.sku]: {
      dataIndex: 'internalCode',
      title: 'Cód. Interno',
    },
  };

  const defaultPartCode = data?.matrixSettings.defaultPartCode;

  const selectedColumn =
    !!defaultPartCode && columnMapping[defaultPartCode]
      ? [
          {
            ...columnMapping[defaultPartCode],
            ellipsis: true,
            width: 140,
          },
        ]
      : [];

  const columns: ColumnsType<PurchaseItems> = [
    ...selectedColumn,
    {
      dataIndex: 'name',
      title: 'Descrição',
      ellipsis: true,
      width: 260,
      render: (value, row) => {
        return (
          <Input
            defaultValue={value}
            onChange={({ target }) =>
              setFieldValue(['name', row.index], target.value)
            }
            disabled={row.linked}
          />
        );
      },
    },
    {
      title: 'Fabricante',
      key: 'manufacturerId',
      filterDropdownOpen: filterManufacturerDropdownOpen,
      onFilterDropdownOpenChange: setFilterManufacturerDropdownOpen,
      filterDropdown: () => (
        <ManufacturerColumnFilter onChange={setManufacturerId} />
      ),
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: PurchaseItems, row) => (
        <ManufacturerInfiniteSelectField
          allowClear
          value={value.manufacturerId}
          onChange={(value) =>
            setFieldValue(['manufacturerId', row.index], value)
          }
          disabled={row.linked}
          style={{
            border: unlinkItems
              .filter((item) => item.index === row.index)
              .every((item) => !item.manufacturerId)
              ? '1px solid red'
              : undefined,
            width: '100%',
          }}
        />
      ),
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Grupo',
      key: 'subGroupId',
      filterDropdownOpen: filterGroupDropdownOpen,
      onFilterDropdownOpenChange: setFilterGroupDropdownOpen,
      filterDropdown: () => <GroupColumnFilter onChange={setSubGroupId} />,
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: PurchaseItems, row) => {
        return (
          <GroupsInfiniteSelectField
            allowClear
            value={value.subGroupId}
            disabled={row.linked}
            onChange={(value) =>
              setFieldValue(['subGroupId', row.index], value)
            }
            style={{
              border: unlinkItems
                .filter((item) => item.index === row.index)
                .every((item) => !item.subGroupId)
                ? '1px solid red'
                : undefined,
              width: '100%',
            }}
          />
        );
      },
      width: 150,
      ellipsis: true,
    },
  ];

  return (
    <>
      <InfiniteTable<PurchaseItems>
        dataSource={unlinkItems?.filter((item) => !item.linked)}
        columns={columns}
        rowKey={(row) => row.key}
        scroll={{ x: getTableScrollX(columns) }}
        loading={!unlinkItems}
      />
    </>
  );
};

export default ConfirmManufacturerAndSubGroup;
